import React, { useState } from 'react';
import { ISignOff, IReportDueDates, IReportingEntity, IBaseComponentProps, IBenefitUpdate } from '../../types';
import { ProjectProgressUpdateReviewList } from '../project/ProjectProgressUpdateReviewList';
import { CrExpandableTextDisplay } from '../cr/CrExpandableTextDisplay';
import { Label } from '@fluentui/react/lib/Label';
import { WorkStreamProgressUpdateReviewList } from '../workStream/WorkStreamProgressUpdateReviewList';
import { MilestoneProgressUpdateReviewList } from '../milestone/MilestoneProgressUpdateReviewList';
import { BenefitProgressUpdateReviewList } from '../benefit/BenefitProgressUpdateReviewList';
import { DependencyProgressUpdateReviewList } from '../dependency/DependencyProgressUpdateReviewList';
import { ProjectProgressUpdateSummaryReviewList } from '../project/ProjectProgressUpdateSummaryReviewList';
import { Toggle } from '@fluentui/react/lib/Toggle';
import { EntityStatus } from '../../refData/EntityStatus';
import { ReportingEntityProgressUpdateReviewList } from '../reportingEntities/ReportingEntityProgressUpdateReviewList';
import { ReportTypes } from '../../refData/ReportTypes';
import { DraftReportHeader } from './DraftReportHeader';
import { UpdateReportingStages, getUpdateReportingStage } from '../../refData/UpdateReportingStages';
import { DateService } from '../../services';

interface IDraftReportProjectProps extends IBaseComponentProps {
    reportDates: IReportDueDates;
    report: Partial<ISignOff>;
    previousReport: ISignOff;
    previousBenefitUpdates: IBenefitUpdate[];
    preHeadingText?: string;
    loadReport: () => void;
    approvedByText?: string;
    showPreviousUpdate?: boolean;
}

export const DraftReportProject = (props: IDraftReportProjectProps): React.ReactElement => {
    const { preHeadingText, report, reportDates, previousReport, previousBenefitUpdates, loadReport } = props;
    //const [showPreviousProjectReport, setShowPreviousProjectReport] = useState(false);

    const isOpen = (entity: IReportingEntity, updatesProp: string): boolean => entity.EntityStatusID === EntityStatus.Open || entity[updatesProp]?.length > 0;

    const stage: string = getUpdateReportingStage(report.Project?.ProjectUpdates?.[0]?.ReportingStageID);
    return (
        <div>
            <DraftReportHeader
                title={`${preHeadingText ?? ''}${report?.Project?.Title}`}
                reportDate={reportDates?.Next}
                reportingStage={stage}
                customFields={report?.Project?.CustomFields?.filter(a => a.CustomFieldDfn?.Display).map(a => a.CustomFieldDfn)}
            />
            <div style={{ marginBottom: '15px'/*, fontSize: '12px'*/ }}>
                Reporting period to {DateService.dateToUkLongDate(reportDates?.Next)} and reporting stage is {stage}. {report.Project?.ProjectUpdates?.[0] && <span>Last updated by {report.Project?.ProjectUpdates?.[0].UpdateUser?.Title} on {DateService.dateToUkLongDate(report.Project?.ProjectUpdates?.[0]?.UpdateDate)}. </span>}{props.approvedByText && <span>{props.approvedByText}</span>}
            </div>
            {/* <Label>Show previous period&apos;s comments</Label>
            <div style={{ display: 'flex', alignItems: 'center' }}>
                <Toggle
                    checked={showPreviousProjectReport}
                    onChange={(_, checked) => setShowPreviousProjectReport(checked)}
                    style={{ marginBottom: '0px' }}
                />
            </div> */}
            <CrExpandableTextDisplay
                label="Project objectives"
                largeLabel={true}
                shortenedCharCount={250}
                text={report.Project?.Objectives}
            />
            <ProjectProgressUpdateReviewList
                {...props}
                project={report.Project}
                projectUpdate={report.Project?.ProjectUpdates?.[0]}
                //only allow edit(pencil icon) if stage is inProgress or not started yet
                readOnly={
                    (report.Project?.ProjectUpdates?.[0]?.ReportingStageID ?? 0) > UpdateReportingStages.UpdatesInProgress
                }

                //readOnly={report.Project?.ProjectUpdates?.[0]?.ReportingStageID > UpdateReportingStages.UpdatesInProgress ?? false}
                previousProjectUpdate={previousReport?.Project?.ProjectUpdates?.[0]}
                showPreviousUpdate={props.showPreviousUpdate}
                onChange={loadReport}
            />
            {report?.ReportingEntityTypes?.filter(ret => ret.IsHeadlineSection).map(ret => ret?.ReportingEntities?.length > 0 &&
                <ReportingEntityProgressUpdateReviewList
                    key={ret.ID}
                    {...props}
                    entities={ret?.ReportingEntities}
                    previousEntities={previousReport?.ReportingEntityTypes?.find(t => t.ID === ret.ID)?.ReportingEntities}
                    onChange={loadReport}
                    entityType={ret}
                />
            )}
            <WorkStreamProgressUpdateReviewList
                {...props}
                //readOnly={report.Project?.ProjectUpdates?.[0]?.ReportingStageID > UpdateReportingStages.UpdatesInProgress ?? false}
                readOnly={
                    (report.Project?.ProjectUpdates?.[0]?.ReportingStageID ?? 0) > UpdateReportingStages.UpdatesInProgress
                }

                entities={report.WorkStreams?.filter(w => isOpen(w, 'WorkStreamUpdates'))}
                previousEntities={previousReport?.WorkStreams}
                showPreviousUpdate={props.showPreviousUpdate}
                onChange={loadReport}
            />
            <BenefitProgressUpdateReviewList
                {...props}
                //readOnly={report.Project?.ProjectUpdates?.[0]?.ReportingStageID > UpdateReportingStages.UpdatesInProgress ?? false}
                readOnly={
                    (report.Project?.ProjectUpdates?.[0]?.ReportingStageID ?? 0) > UpdateReportingStages.UpdatesInProgress
                }

                entities={report.Benefits?.filter(b => isOpen(b, 'BenefitUpdates'))}
                previousEntities={previousReport?.Benefits}
                previousBenefitUpdates={previousBenefitUpdates}
                showPreviousUpdate={props.showPreviousUpdate}
                onChange={loadReport}
            />
            <MilestoneProgressUpdateReviewList
                {...props}
                //readOnly={report.Project?.ProjectUpdates?.[0]?.ReportingStageID > UpdateReportingStages.UpdatesInProgress ?? false}
                readOnly={
                    (report.Project?.ProjectUpdates?.[0]?.ReportingStageID ?? 0) >
                    UpdateReportingStages.UpdatesInProgress
                }

                entities={report.Milestones?.filter(m => isOpen(m, 'MilestoneUpdates'))}
                previousEntities={previousReport?.Milestones}
                showPreviousUpdate={props.showPreviousUpdate}
                onChange={loadReport}
                reportType={ReportTypes.Project}
            />
            <DependencyProgressUpdateReviewList
                {...props}
                //readOnly={report.Project?.ProjectUpdates?.[0]?.ReportingStageID > UpdateReportingStages.UpdatesInProgress ?? false}
                readOnly={
                    (report.Project?.ProjectUpdates?.[0]?.ReportingStageID ?? 0) >
                    UpdateReportingStages.UpdatesInProgress
                }

                entities={report.Dependencies?.filter(d => isOpen(d, 'DependencyUpdates'))}
                previousEntities={previousReport?.Dependencies}
                showPreviousUpdate={props.showPreviousUpdate}
                onChange={loadReport}
            />
            <ProjectProgressUpdateSummaryReviewList
                {...props}
                //readOnly={report.Project?.ProjectUpdates?.[0]?.ReportingStageID > UpdateReportingStages.UpdatesInProgress ?? false}
                readOnly={
                    (report.Project?.ProjectUpdates?.[0]?.ReportingStageID ?? 0) >
                    UpdateReportingStages.UpdatesInProgress
                }
                listTitle="Project progress updates"
                entities={report.Projects?.filter(p => isOpen(p, 'ProjectUpdates'))}
                previousEntities={previousReport?.Projects}
                showPreviousUpdate={props.showPreviousUpdate}
                onChange={loadReport}
            />
            {report?.ReportingEntityTypes?.filter(ret => !ret.IsHeadlineSection).map(ret => ret?.ReportingEntities?.length > 0 &&
                <ReportingEntityProgressUpdateReviewList
                    key={ret.ID}
                    {...props}
                    entities={ret?.ReportingEntities}
                    previousEntities={previousReport?.ReportingEntityTypes?.find(t => t.ID === ret.ID)?.ReportingEntities}
                    onChange={loadReport}
                    entityType={ret}
                />
            )}
        </div>
    );
};
